import "../ChartBrand.css";
import "./PieChart.css";

import { ActiveElement, Chart as ChartJS, ChartEvent } from "chart.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Doughnut } from "react-chartjs-2";

import { thousandsFormatter } from "../../../utils";
import { useChartVariant } from "../ChartVariantContext";
import { ChartDataWithSuffix, ChartProps, PieChartCenterTextFont, PieChartColors } from "../models";
import { ChartVariant, formatShareOfTotal, getChartTheme, maybeSetGlobalChartJsConfiguration } from "../utils";
import { ChartLegend, ChartLegendWithSuffix } from ".";
import { ChartTooltip } from "./components";
import { usePieChart } from "./usePieChart";

maybeSetGlobalChartJsConfiguration();

/** Legend emphasis: `"Primary"` shows a colour swatch; `"Secondary"` colours the value text instead. */
type PieChartEmphasis = "Primary" | "Secondary";

export interface PieChartProps extends ChartProps {
  size: number;
  /** Legend emphasis (swatch vs coloured value). Renamed from the former `variant` prop. */
  emphasis?: PieChartEmphasis;
  /**
   * Design-system brand: `"hub"` (square segments/keys) or `"seed"` (rounded).
   * Defaults to the nearest ChartVariantProvider (Hub if none).
   */
  variant?: ChartVariant;
  centerTextSuffix?: string;
  centerTextPrefix?: string;
  centerTextHidden?: boolean;
  /** Overrides the brand's centre typeface; leave unset to follow the variant. */
  centerTextFont?: PieChartCenterTextFont;
  noDataMessage?: string;
  hasSuffix?: boolean;
  dataWithSuffix?: ChartDataWithSuffix;
  isGradient?: boolean;
  direction?: "Horizontal" | "Vertical";
  colors?: PieChartColors;
  chartColorsOverride?: string[][];
  showValues?: boolean;
  defaultLegendValue?: string | null;
  /** Set false when the consumer supplies its own legend (e.g. an expandable one). */
  showLegend?: boolean;
  /**
   * Index of the highlighted segment, for a consumer supplying its own key — without it
   * a chart rendered with `showLegend={false}` has no way to light a segment up.
   * Leave unset and the chart tracks its own.
   */
  activeSegment?: number | null;
  /** Reports what is being pointed at, whether that came from the chart or the key. */
  onActiveSegmentChange?: (index: number | null) => void;
}

export const PieChart = ({
  data,
  size,
  emphasis = "Primary",
  variant,
  centerTextPrefix,
  centerTextSuffix,
  centerTextHidden = false,
  centerTextFont,
  noDataMessage = "No data",
  hasSuffix = false,
  dataWithSuffix,
  isGradient = false,
  direction = "Horizontal",
  colors = "default",
  chartColorsOverride,
  showValues = true,
  defaultLegendValue = null,
  showLegend = true,
  activeSegment,
  onActiveSegmentChange,
}: PieChartProps): JSX.Element => {
  const contextVariant = useChartVariant();
  const theme = getChartTheme(variant ?? contextVariant);
  const { chartData, legendData, chartColours, plugins, options, hasData, pieChartRef } = usePieChart(
    data,
    centerTextPrefix,
    centerTextSuffix,
    centerTextHidden,
    centerTextFont ?? theme.centerTextFont,
    noDataMessage,
    isGradient,
    colors,
    chartColorsOverride,
    theme
  );

  // Controlled when the consumer supplies its own key, otherwise the chart tracks it.
  const isControlled = activeSegment !== undefined;
  const [ownActiveIndex, setOwnActiveIndex] = useState<number | null>(null);
  const activeIndex = isControlled ? activeSegment : ownActiveIndex;
  const chartInstance = useRef<ChartJS<"doughnut"> | null>(null);
  const isLegendDriving = useRef(false);
  // Whether the pending highlight came from pointing at the canvas, as opposed to a key
  // row — the chart's own or a consumer's driving it through activeSegment.
  const isChartDriving = useRef(false);
  // Set while the pointer is away from the canvas. Clearing the highlight makes Chart.js
  // re-evaluate against the position it last saw — which is still over a slice — so
  // without this the echo puts the highlight straight back.
  const isPointerOffCanvas = useRef(false);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  const setChartNode = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (node: any) => {
      chartInstance.current = node;
      pieChartRef(node);
    },
    [pieChartRef]
  );

  const setActiveIndex = useCallback(
    (index: number | null) => {
      if (!isControlled) setOwnActiveIndex(index);
      onActiveSegmentChange?.(index);
    },
    [isControlled, onActiveSegmentChange]
  );

  // Chart.js only calls onHover while the event is inside the chart area, so moving
  // straight off a slice — or lifting a finger — produces no callback at all and would
  // leave the tooltip and the dimmed key frozen.
  const clearChartHover = useCallback(() => {
    isPointerOffCanvas.current = true;
    // Unconditional: moving onto a key row fires this first and the row's own enter
    // immediately after, so the key never loses a highlight it is entitled to.
    setActiveIndex(null);
    setPointer(null);
  }, [setActiveIndex]);

  const setActiveFromLegend = useCallback(
    (index: number | null) => {
      isLegendDriving.current = index !== null;
      // One tooltip per hover: the key row is already showing the name, so the floating
      // one would only repeat it — and beside the donut, not where you are pointing.
      setPointer(null);
      setActiveIndex(index);
    },
    [setActiveIndex]
  );

  // A highlight that did not come from the canvas gets no floating tooltip: the key row
  // is already showing the name, and the tooltip would sit by the donut rather than
  // where the pointer is. This covers a consumer driving activeSegment too, where the
  // chart cannot tell an echo of its own setActiveElements from a real hover.
  useEffect(() => {
    if (!isChartDriving.current) setPointer(null);
    isChartDriving.current = false;
  }, [activeIndex]);

  // A live dataset can empty out under the pointer. The hover guard only turns away new
  // events; a highlight already showing would be re-read against the placeholder and
  // name itself "No data — 0 (0%)", so it has to be retired here.
  useEffect(() => {
    if (hasData) return;
    setActiveIndex(null);
    setPointer(null);
  }, [hasData, setActiveIndex]);

  // Pointing at a legend row highlights the matching segment, and vice versa.
  useEffect(() => {
    const chart = chartInstance.current;
    if (!hasData || !chart) return;

    // The index can outlive its segment when the data swaps to fewer of them, and Chart.js restyles
    // whatever it is handed without checking - so a vanished segment clears the highlight instead.
    const hasSegment = activeIndex !== null && chart.getDatasetMeta(0)?.data[activeIndex] !== undefined;
    chart.setActiveElements(hasSegment ? [{ datasetIndex: 0, index: activeIndex }] : []);
    chart.update();
  }, [activeIndex, hasData]);

  const linkedOptions = useMemo(
    () => ({
      ...options,
      onHover: (event: ChartEvent, elements: ActiveElement[]) => {
        // While the key owns the highlight, Chart.js is only echoing the active element
        // we set on it — it reports "nothing hovered" when the pointer sits over the key,
        // and reports that element as hovered once update() runs. Neither is the user
        // pointing at the canvas, so leave the key's highlight alone.
        if (isLegendDriving.current || isPointerOffCanvas.current) return;
        // An empty chart still draws a one-unit placeholder arc that Chart.js reports as
        // hoverable; naming it would offer "No data — 0 (0%)".
        if (!hasData) return;

        isChartDriving.current = true;
        setActiveIndex(elements[0]?.index ?? null);
        // ChartEvent's own x/y, not the native event's offsets: on a touch device the
        // native event is a TouchEvent and has no offsetX/offsetY, which would leave the
        // tooltip unpositioned.
        const { x, y } = event;
        setPointer(elements.length && x !== null && y !== null ? { x, y } : null);
      },
    }),
    [options, setActiveIndex, hasData]
  );

  const segment = useMemo(() => {
    const suffixed = hasSuffix && dataWithSuffix ? dataWithSuffix : undefined;
    const labels = Object.keys(suffixed ?? legendData);
    const name = activeIndex === null ? null : (labels[activeIndex] ?? null);
    if (!name || !pointer) return null;

    const valueOf = (key: string): number => suffixed?.[key].value ?? legendData[key];
    const total = labels.reduce((sum, key) => sum + valueOf(key), 0);
    const prefix = suffixed?.[name].prefix ?? "";
    // The key sets a suffix off from its value — "120 ha" — and the tooltip is naming the
    // same quantity, so it spaces it the same way. centerTextSuffix stays compact: it is
    // the centre figure's own unit, drawn tight against it.
    const rawSuffix = suffixed?.[name].suffix;
    const spacedSuffix = rawSuffix ? ` ${rawSuffix}` : "";
    const suffix = suffixed ? spacedSuffix : (centerTextSuffix ?? "");
    const value = `${prefix}${thousandsFormatter.format(valueOf(name)).toLowerCase()}${suffix}`;

    return { name, detail: `${value} (${formatShareOfTotal(valueOf(name), total)})` };
  }, [activeIndex, pointer, legendData, dataWithSuffix, hasSuffix, centerTextSuffix]);

  const legend =
    hasSuffix && dataWithSuffix ? (
      <ChartLegendWithSuffix
        colours={chartColours}
        hasData={hasData}
        defaultValue={defaultLegendValue}
        data={dataWithSuffix}
        showValues={showValues}
        keyBorderRadius={theme.legendKeyBorderRadius}
        activeIndex={activeIndex}
        onActiveIndexChange={setActiveFromLegend}
        variant={theme.variant}
      />
    ) : (
      <ChartLegend
        data={legendData}
        colours={chartColours}
        hasData={hasData}
        defaultValue={defaultLegendValue}
        emphasis={emphasis}
        keyBorderRadius={theme.legendKeyBorderRadius}
        showValues={showValues}
        activeIndex={activeIndex}
        onActiveIndexChange={setActiveFromLegend}
        variant={theme.variant}
      />
    );

  const chart = (usePlugins: boolean): JSX.Element => (
    <div className={`PieChart ChartBrand_${theme.variant} ${emphasis} ${direction} ${showLegend ? "" : "NoLegend"}`}>
      {/* The canvas retires its own hover: clearing only when the active segment changed
          stranded the tooltip when the pointer moved onto the key row for the segment it
          was already showing. */}
      <div
        className="Chart"
        style={{ maxWidth: size, maxHeight: size }}
        onMouseEnter={() => {
          isPointerOffCanvas.current = false;
        }}
        onMouseLeave={clearChartHover}
        onTouchEnd={clearChartHover}
        onTouchCancel={clearChartHover}
      >
        <Doughnut
          /* Only the plugin-bearing chart is tracked. The empty-data chart can render beside it for a
             moment, and sharing the ref would let its mount and unmount overwrite the live instance,
             which react-chartjs-2 never forwards again. */
          ref={usePlugins ? setChartNode : undefined}
          data={chartData}
          options={linkedOptions}
          plugins={usePlugins ? plugins : undefined}
        />
        {segment && pointer && (
          <ChartTooltip placement="AtPointer" variant={theme.variant} style={{ left: pointer.x, top: pointer.y }}>
            <p className="body3">{segment.name}</p>
            <p className="body3">{segment.detail}</p>
          </ChartTooltip>
        )}
      </div>
      {showLegend && legend}
    </div>
  );

  return (
    <>
      {plugins && chart(true)}
      {(data === null || data === undefined || Object.values(data).length === 0) && chart(false)}
    </>
  );
};
